import { useEffect, useRef, useState } from 'react';

import type { Product } from '../types/domain';

interface Props {
  products: Product[];
  value: string;
  // Exact barcode matches include the barcode so callers can retain its unit.
  onChange: (id: string, barcode?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function ProductSelect({
  products,
  value,
  onChange,
  placeholder = 'ค้นหาด้วยชื่อ หรือยิงบาร์โค้ด...',
  disabled = false,
  className = '',
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Camera scanner states
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);

  // Sync search input with the selected product's name
  useEffect(() => {
    const selected = products.find((p) => p.id === value);
    if (selected) {
      setSearch(selected.name);
    } else {
      setSearch('');
    }
  }, [value, products]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset search to selected name if closed without selecting
        const selected = products.find((p) => p.id === value);
        setSearch(selected ? selected.name : '');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, products]);

  const playBeep = () => {
    try {
      const AudioCtx =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1000, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch (e) {
      console.warn('Audio beep failed', e);
    }
  };

  const handleBarcodeSearch = (code: string) => {
    const clean = code.trim();
    if (!clean) return false;
    const barcodeMatch = products.find((p) =>
      p.units.some((u) => u.barcode === clean),
    );
    if (barcodeMatch) {
      onChange(barcodeMatch.id, clean);
      playBeep();
      setIsOpen(false);
      return true;
    }
    return false;
  };

  // Camera scanner hook
  useEffect(() => {
    if (!isScanning) return;

    let stream: MediaStream | null = null;
    let scanInterval: any = null;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current
            .play()
            .catch((e) => console.warn('Video play error', e));
        }

        if ('BarcodeDetector' in window) {
          const detector = new (window as any).BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'qr_code', 'code_128'],
          });

          scanInterval = setInterval(async () => {
            if (
              videoRef.current &&
              videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA
            ) {
              try {
                const codes = await detector.detect(videoRef.current);
                if (codes.length > 0) {
                  const raw = codes[0].rawValue;
                  const matched = handleBarcodeSearch(raw);
                  if (matched) {
                    setIsScanning(false);
                  }
                }
              } catch (e) {
                console.error('Barcode detection error', e);
              }
            }
          }, 400);
        }
      })
      .catch((err) => {
        console.error('Camera access error', err);
        setCameraError(
          'ไม่สามารถเข้าถึงกล้องถ่ายรูปได้ กรุณาตรวจสอบสิทธิ์การเข้าใช้งานกล้องในเบราว์เซอร์ของคุณ',
        );
      });

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (scanInterval) {
        clearInterval(scanInterval);
      }
    };
  }, [isScanning]);

  const query = search.trim().toLowerCase();

  // Filter products based on search term
  const filtered = products
    .filter((p) => {
      if (p.id === value) return true;
      if (!query) return true;
      // Match name or barcode of any units
      return (
        p.name.toLowerCase().includes(query) ||
        p.units.some(
          (u) => u.barcode && u.barcode.toLowerCase().includes(query),
        )
      );
    })
    .slice(0, query ? undefined : 50); // limit when empty to avoid lag

  const handleSelect = (id: string) => {
    onChange(id);
    setIsOpen(false);
  };

  const handleInputChange = (val: string) => {
    setSearch(val);
    setIsOpen(true);

    // Auto-select if there is an exact barcode match
    const clean = val.trim();
    if (clean) {
      const barcodeMatch = products.find((p) =>
        p.units.some((u) => u.barcode === clean),
      );
      if (barcodeMatch) {
        onChange(barcodeMatch.id, clean);
        setIsOpen(false);
      }
    }
  };

  return (
    <div ref={wrapperRef} className={`relative w-full ${className}`}>
      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          placeholder={placeholder}
          value={search}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => handleInputChange(e.target.value)}
          className="h-10 w-full pl-3 pr-[92px] rounded-btn border border-line bg-surface text-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-bg-warm disabled:text-ink-3 transition-all"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1.5">
          {/* Clear button if value exists */}
          {value && !disabled && (
            <button
              type="button"
              aria-label="ล้างสินค้าที่เลือก"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setSearch('');
                setIsOpen(true);
              }}
              className="text-ink-3 hover:text-danger w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/5 transition-all text-xs"
            >
              ✕
            </button>
          )}
          {/* Barcode scanner button */}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsScanning(true);
                setCameraError('');
              }}
              className="h-7 w-7 rounded bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition-all active:scale-95 shadow-sm"
              title="สแกนกล้อง"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v1m6 11h2m-6 0h-2v4m0-16v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                />
              </svg>
            </button>
          )}
          {/* Dropdown indicator icon */}
          <span className="text-ink-3 pr-1">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto bg-surface border border-line rounded-btn shadow-modal bo-fade">
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-ink-3 text-center">
              ไม่พบสินค้า
            </div>
          ) : (
            filtered.map((p) => {
              const isSelected = p.id === value;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p.id)}
                  className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors border-b border-line-2 last:border-0 flex items-center justify-between hover:bg-bg-warm/40 ${
                    isSelected
                      ? 'bg-primary-light text-primary font-bold'
                      : 'text-ink'
                  }`}
                >
                  <div className="flex flex-col">
                    <span>{p.name}</span>
                    <span className="text-[10px] text-ink-3">
                      {p.units
                        .map(
                          (u) =>
                            `${u.unit}${u.barcode ? ` (${u.barcode})` : ''}`,
                        )
                        .join(' · ')}
                    </span>
                  </div>
                  {isSelected && <span className="text-primary">✓</span>}
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Camera scan fullscreen interface */}
      {isScanning && (
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center">
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @keyframes scanLaser {
              0% { top: 0%; }
              50% { top: 100%; }
              100% { top: 0%; }
            }
          `,
            }}
          />

          {/* Scanning Guide Overlay */}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-between py-8 px-4 text-center">
            <div className="flex justify-between items-center w-full max-w-md">
              <h3 className="text-white text-base font-extrabold">
                สแกนบาร์โค้ดสินค้า
              </h3>
              <button
                type="button"
                onClick={() => setIsScanning(false)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-lg font-bold"
              >
                ✕
              </button>
            </div>

            {/* Scanning Target Box */}
            <div className="relative w-64 h-64 border-2 border-white/40 rounded-xl overflow-hidden shadow-[0_0_0_2000px_rgba(0,0,0,0.6)]">
              {/* Laser Line */}
              <div className="absolute left-0 right-0 h-0.5 bg-danger shadow-[0_0_8px_rgba(200,54,42,0.8)] animate-[scanLaser_2s_infinite]"></div>
            </div>

            <div className="text-white text-sm font-semibold max-w-xs leading-relaxed bg-black/40 py-2 px-4 rounded-chip">
              วางบาร์โค้ดให้อยู่ในกรอบสี่เหลี่ยมเพื่อสแกนอัตโนมัติ
            </div>
          </div>

          {/* Camera Video Feed */}
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />

          {cameraError && (
            <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center z-30">
              <span className="text-danger text-lg font-bold mb-2">
                เกิดข้อผิดพลาดในการโหลดกล้อง
              </span>
              <span className="text-white text-sm mb-6 max-w-sm">
                {cameraError}
              </span>
              <button
                type="button"
                className="h-10 px-5 rounded-btn bg-surface text-ink font-semibold border border-line hover:bg-bg-warm transition-colors"
                onClick={() => setIsScanning(false)}
              >
                ปิดหน้าต่างสแกน
              </button>
            </div>
          )}

          {/* Simulated Scanner Fallback */}
          {!('BarcodeDetector' in window) && !cameraError && (
            <div className="absolute bottom-6 left-4 right-4 bg-black/90 text-white p-4 rounded-xl text-xs flex flex-col gap-2.5 z-20 border border-white/10 max-w-md mx-auto">
              <div className="flex gap-1.5 items-center text-warn font-bold">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>เบราว์เซอร์นี้สแกนบาร์โค้ดด้วยกล้องไม่ได้</span>
              </div>
              <span className="text-white/60">
                กรุณาพิมพ์เลขบาร์โค้ด หรือใช้เครื่องยิงบาร์โค้ดแทน
              </span>
              {import.meta.env.DEV && (
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBarcodeSearch(e.target.value);
                      setIsScanning(false);
                    }
                  }}
                  className="bg-white/10 border border-white/20 text-white h-9 px-3 rounded-btn text-xs w-full focus:outline-none"
                >
                  <option value="" className="bg-ink">
                    --- เลือกสินค้าเพื่อจำลองการสแกน ---
                  </option>
                  {products.map((p) =>
                    p.units.map((u) => (
                      <option
                        key={u.barcode}
                        value={u.barcode}
                        className="bg-ink"
                      >
                        {p.name} ({u.unit}) - {u.barcode}
                      </option>
                    )),
                  )}
                </select>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
